import { Layout, Text } from '@oneaudi/unified-web-components';
import { htmlToText } from 'html-to-text';
import { ConfiguredFeatureStyle } from '~/app/compositions/content/components/styles/configured-feature-style';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig, VATConfig } from '~/app/market-config';
import { useDebugMode } from '~/app/utils/debug-mode';
import { useItemQuantity } from '~/components/product-information';
import { FeatureOrPackage } from '~/graphql/types';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import MonthlyRateFootnote from '../calculation-info/components/monthly-rate-footnote';
import { useFinancing } from '~/graphql/hooks/use-financing';

export function ConfiguredFeature({
	item,
	moreInformation,
	vatConfig,
	disclaimer,
}: {
	item: FeatureOrPackage;
	vatConfig: VATConfig;
	moreInformation: JSX.Element;
	disclaimer?: string;
}) {
	const { formatCurrency, formatMonthlyRate } = useCurrencyFormatter();
	const { partRateByPr } = useFinancing();
	const { name, status, price, pr7 } = item;
	const { config } = useMarketConfig();
	const quantity = useItemQuantity(item);
	const formattedRate = formatMonthlyRate(partRateByPr(pr7));
	const { getName } = useDebugMode();
	const { i18n } = useI18n();

	return (
		<ConfiguredFeatureStyle>
			<Layout direction="column" rowGap="4px">
				<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
					{getName({ name: htmlToText(name || ''), pr7 })}
				</Text>
				{quantity && (
					<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
						({quantity.formattedValue})
					</Text>
				)}
			</Layout>
			<Layout direction="column">
				{config.showPrices && (
					<Text tag={'p'} variant={{ type: 'copy', order: '2', style: 'normal' }} className="alignRight">
						{status.standard && i18n('ngc.equipment.standard')}
						{!status.standard && typeof price?.value === 'number' && formatCurrency(price, vatConfig)}
					</Text>
				)}
				{formattedRate && !config.hideMonthlyRates && config.showPrices && (
					<Text tag={'p'} variant={{ type: 'copy', order: '2', style: 'normal' }} className="alignRight">
						{formattedRate}
						<MonthlyRateFootnote text={disclaimer} />
					</Text>
				)}
			</Layout>
			{moreInformation}
		</ConfiguredFeatureStyle>
	);
}
